import { useState } from "react";
import Banner from "./sections/PageBanner";
import PageHeader from "./sections/PageHeader";
import Footer from "./sections/PageFooter";
import ReactGA from "react-ga4";

ReactGA.initialize("G-FMCF2WQ3F6");

/* ── FAQ data (deduplicated & grouped) ──────────────────────────────── */
const categories = [
  {
    id:    'about',
    label: 'About WITS',
    items: [
      {
        q: 'What is WITS?',
        a: 'WITS stands for Wildlife Information & Tracking System — a secure, data-driven platform that collects, tracks, and analyses wildlife-related incidents including poaching, habitat loss, illegal trade, and human-wildlife conflict to support evidence-based conservation.',
      },
      {
        q: 'Why was WITS created?',
        a: 'WITS was created to change the narrative of conservation from anecdotal effort to evidence-based action, measured impact, and collective intelligence — making conservation more accountable, efficient, inclusive, and community-driven.',
      },
      {
        q: 'Who can use the platform?',
        a: 'Anyone — community members, conservationists, government officials, NGOs, researchers, and volunteers. WITS welcomes both anonymous and verified users across all levels of expertise.',
      },
      {
        q: 'Is WITS free to use?',
        a: 'Yes. WITS is free for individuals and non-profit conservation use. Institutional access and API integrations may have separate agreements.',
      },
    ],
  },
  {
    id:    'reporting',
    label: 'What to Report',
    items: [
      {
        q: 'What kinds of incidents can I report?',
        a: 'WITS accepts reports across six categories: Wildlife Crimes (poaching, trafficking, retaliatory killings, banned traps); Forestry & Land Crimes (illegal logging, charcoal, encroachment); Fisheries & Marine Crimes (IUU fishing, explosives, bycatch); Pollution & Waste Crimes (toxic dumping, oil spills); Climate & Resource Crimes (mining in protected areas, peatland destruction); and Governance Crimes (permit corruption, EIA tampering, CITES forgery).',
      },
      {
        q: 'Can I report bushmeat trade or retaliatory wildlife killings?',
        a: 'Yes. Both are within scope. Bushmeat trade falls under wildlife trafficking, and retaliatory killings can be reported under the Human-Wildlife Conflict category.',
      },
      {
        q: 'Can habitat encroachments and wildlife poisoning be reported?',
        a: 'Yes. Illegal settlement, logging, or farming in protected areas, as well as poisoning, snaring, or any deliberate harm to wildlife, are all reportable.',
      },
    ],
  },
  {
    id:    'privacy',
    label: 'Privacy & Security',
    items: [
      {
        q: 'Is it safe to report incidents on WITS?',
        a: 'Yes. WITS has a strict Whistleblower Protection Policy. All reports are confidential and you can remain fully anonymous. We use end-to-end encryption and secure data handling to protect your identity and submissions.',
      },
      {
        q: 'Can I report anonymously?',
        a: 'Yes. You can choose to report anonymously or share your identity if you wish to receive follow-up. Anonymous reporters will never be contacted.',
      },
      {
        q: 'Who can access the data I submit?',
        a: 'Data is accessed only by authorized personnel or partner organizations, depending on your privacy settings. No personal data is shared without explicit consent.',
      },
      {
        q: 'How does WITS ensure data security?',
        a: 'WITS uses encryption, secure servers, and blockchain technology to protect data integrity and user privacy throughout the reporting and verification chain.',
      },
    ],
  },
  {
    id:    'platform',
    label: 'Using the Platform',
    items: [
      {
        q: 'How do I submit a report?',
        a: 'Log in, select "Report Incident," fill in the required details, attach any media (photos, videos, voice notes), and submit. Reports can also be submitted via the mobile app or through authorized community partners.',
      },
      {
        q: 'Can I attach photos or videos with my report?',
        a: 'Yes — multimedia uploads are strongly encouraged. Visual evidence significantly improves verification accuracy and the speed of response.',
      },
      {
        q: 'Can I track the status of my report?',
        a: 'Yes. Once submitted, you can log in to view real-time status updates: under review, verified, or acted upon.',
      },
      {
        q: 'Can I edit a report after submission?',
        a: 'Reports cannot be edited once submitted, but you can submit additional information as a follow-up. The WITS admin team reviews all submissions.',
      },
      {
        q: 'Is there a mobile version of WITS?',
        a: 'Yes. WITS is fully mobile-friendly. A dedicated Android and iOS app is coming soon, and an offline data collection tool is in development for areas without network coverage.',
      },
    ],
  },
  {
    id:    'data',
    label: 'Data & Analytics',
    items: [
      {
        q: 'How is submitted data verified?',
        a: 'Reports go through a validation process that may include field checks, cross-referencing with partner data, and expert review by the WITS moderation team.',
      },
      {
        q: 'Can I view incidents on a map?',
        a: 'Yes. WITS features a GIS-enabled dashboard with interactive heatmaps and spatial incident data visible to users with appropriate access.',
      },
      {
        q: 'Can I access statistics or download data?',
        a: 'Limited aggregated data is publicly available. Detailed datasets may require access approval and, in some cases, a data-sharing agreement.',
      },
      {
        q: 'Can I use WITS data for research?',
        a: 'Yes, upon approval and subject to a data-sharing agreement with the WITS team.',
      },
      {
        q: 'Can organizations integrate their data into WITS?',
        a: 'Yes. WITS supports integration with existing data systems via secure APIs. Contact the tech team to explore options.',
      },
    ],
  },
  {
    id:    'community',
    label: 'Community & Partnerships',
    items: [
      {
        q: 'How can rangers use WITS?',
        a: 'Rangers can log poaching incidents, patrol data, and habitat disturbances in real time, contributing directly to the central intelligence database.',
      },
      {
        q: 'How can my community or school start using WITS?',
        a: 'Contact the WITS outreach team for onboarding and training. Schools can also incorporate WITS into environmental clubs or citizen science programmes.',
      },
      {
        q: 'Can I volunteer or intern with WITS?',
        a: 'Yes. Volunteer roles in reporting, outreach, and technical support are available via the "Get Involved" section. Internship opportunities are posted on the WITS website.',
      },
      {
        q: 'Can my organisation partner with WITS?',
        a: 'Yes. We welcome partnerships with conservation NGOs, government agencies, and research institutions. Reach out via the Contact page to start a conversation.',
      },
      {
        q: 'Is WITS available in local languages?',
        a: 'The platform is being actively translated into Swahili and other regional languages to increase accessibility across East and Southern Africa.',
      },
    ],
  },
  {
    id:    'tech',
    label: 'Technology',
    items: [
      {
        q: 'What technologies power WITS?',
        a: 'WITS integrates GIS mapping, AI, machine learning, and blockchain for intelligent data analysis, secure recordkeeping, and spatial crime intelligence.',
      },
      {
        q: 'Is WITS open-source?',
        a: 'Yes. Core components of WITS are open-source to encourage transparency and collaboration. Developers can contribute via GitHub.',
      },
      {
        q: 'Is WITS scalable to other countries or regions?',
        a: 'Yes. The platform is designed for scalability and can be localised to any region, with language support and jurisdiction-specific legal frameworks.',
      },
      {
        q: 'How does WITS support conservation policy?',
        a: 'WITS provides verified, real-time data that policymakers can use to craft targeted wildlife protection laws, identify enforcement gaps, and measure intervention outcomes.',
      },
    ],
  },
];

/* ── Page hero ──────────────────────────────────────────────────────── */
function PageHero() {
  return (
    <div
      className="relative w-full overflow-hidden flex flex-col justify-end"
      style={{ height: '46vh', minHeight: 320, background: '#052e16' }}
    >
      <img
        src="/images/v1/img12.jpg"
        alt=""
        className="absolute inset-0 w-full h-full object-cover select-none"
        style={{ opacity: 0.28 }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#052e16]/20 via-transparent to-[#052e16]/95 pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pb-12">
        <span className="flex items-center gap-3 mb-4">
          <span className="h-px w-8 bg-green-400" />
          <span className="text-green-400 text-xs tracking-[0.22em] uppercase font-medium" style={{ fontFamily: 'var(--font-body)' }}>
            Help Centre
          </span>
        </span>
        <h1
          className="text-5xl lg:text-6xl font-bold text-white leading-tight"
          style={{ fontFamily: 'var(--font-display)', textShadow: '0 2px 24px rgba(0,0,0,0.5)' }}
        >
          Frequently Asked <span className="text-green-400">Questions</span>
        </h1>
        <p
          className="mt-4 text-white/65 text-base max-w-xl leading-relaxed"
          style={{ fontFamily: 'var(--font-body)', textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}
        >
          Everything you need to know about WITS — from reporting an incident to partnering with us.
        </p>
      </div>
    </div>
  );
}

/* ── Accordion item ─────────────────────────────────────────────────── */
function AccordionItem({ q, a, open, onToggle }: { q: string; a: string; open: boolean; onToggle: () => void }) {
  return (
    <div className={`border rounded-xl overflow-hidden transition-all duration-300 ${open ? 'border-green-300 shadow-sm shadow-green-100/60' : 'border-gray-200'}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left bg-white hover:bg-green-50/50 transition-colors"
        aria-expanded={open}
      >
        <span
          className="text-gray-900 font-medium text-sm leading-snug"
          style={{ fontFamily: 'var(--font-body)' }}
        >
          {q}
        </span>
        <span
          className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${open ? 'bg-green-600 text-white rotate-45' : 'bg-gray-100 text-gray-500'}`}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-3.5 h-3.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="px-6 pb-5 pt-1 bg-white border-t border-gray-100">
          <p
            className="text-gray-600 text-sm leading-relaxed whitespace-pre-line"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            {a}
          </p>
        </div>
      )}
    </div>
  );
}

/* ── FAQ body ───────────────────────────────────────────────────────── */
function FAQBody() {
  const [activeCategory, setActiveCategory] = useState('about');
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const current = categories.find((c) => c.id === activeCategory) ?? categories[0];

  const handleCategory = (id: string) => {
    setActiveCategory(id);
    setOpenIndex(0);
  };

  return (
    <div className="w-full bg-[#fafaf7] py-16 lg:py-24">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mb-12">
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => handleCategory(c.id)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 ${
                activeCategory === c.id
                  ? 'bg-green-700 text-white shadow-sm'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-green-300 hover:text-green-700'
              }`}
              style={{ fontFamily: 'var(--font-body)' }}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Category heading */}
        <div className="mb-8">
          <h2
            className="text-2xl font-bold text-gray-900"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {current.label}
          </h2>
          <p className="text-gray-400 text-sm mt-1" style={{ fontFamily: 'var(--font-body)' }}>
            {current.items.length} questions
          </p>
        </div>

        {/* Accordion */}
        <div className="flex flex-col gap-3">
          {current.items.map((item, i) => (
            <AccordionItem
              key={i}
              q={item.q}
              a={item.a}
              open={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>

        {/* CTA */}
        <div className="mt-14 p-8 rounded-2xl text-center" style={{ background: '#052e16', border: '1px solid rgba(74,222,128,0.15)' }}>
          <p className="text-white font-semibold text-lg mb-2" style={{ fontFamily: 'var(--font-display)' }}>
            Still have questions?
          </p>
          <p className="text-white/55 text-sm mb-6" style={{ fontFamily: 'var(--font-body)' }}>
            Our team is happy to help. Reach out and we&apos;ll get back to you within 1&ndash;2 business days.
          </p>
          <a
            href="/contact-us"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-green-600 text-white text-sm font-semibold hover:bg-green-500 transition-all duration-300 hover:-translate-y-0.5"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            Contact Us &rarr;
          </a>
        </div>

      </div>
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────── */
export default function FAQs() {
  ReactGA.send({ hitType: "pageview", page: "/faqs", title: "FAQs" });
  return (
    <>
      <Banner />
      <PageHeader />
      <PageHero />
      <FAQBody />
      <Footer />
    </>
  );
}
